import { Theme } from 'contexts/Theme';
import styled from 'styled-components';
import sizes from 'styles/sizes';
import { BrandCode } from 'types/Brand';

export const RefinedCruiseLoadingSkeletonBase = styled.div`
  display: flex;
  min-height: 310px;
  flex-direction: column;
  align-items: flex-start;
  border-radius: 10px;
  box-shadow: 0px 0px 8px 0px rgba(0, 0, 0, 0.10);

  &:not(:last-child) {
    margin-bottom: 30px;
  }

  ${({ theme }: { theme: Theme }) => `
    border: 1px solid ${theme.palette.grey[300]};
    background-color: ${theme.palette.common.white};
  `}

  @media (min-width: ${sizes.media.tabletLandscape}px) {
    flex-direction: row;
    min-height: 310px;
    align-items: flex-start;
  }
`;

export const RefinedCruiseLoadingSkeletonImage = styled.div`
  display: flex;
  height: 250px;
  padding: 20px;
  flex-direction: column;
  justify-content: space-between;
  align-items: flex-start;
  align-self: stretch;
  background-color: ${({ theme }: { theme: Theme }) => theme.palette.grey[300]};

  ${({ theme }) => ({
    [BrandCode.Celebrity]: `
      height: 230px;

      @media (min-width: ${sizes.media.tabletPortrait}px) {
        height: 251px;
      }
    `,
    [BrandCode.RoyalCaribbean]: `
      height: 250px;
    `,
  })[theme.brandCode]}

  @media (min-width: ${sizes.media.tabletLandscape}px) {
    height: auto;
    min-width: 55%;
    padding: 20px 0px;
    flex-shrink: 0;
    align-self: stretch;
  }
`;

export const RefinedCruiseLoadingSkeletonMain = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  align-self: stretch;
  flex: 1;

  ${({ theme }) => ({
    [BrandCode.Celebrity]: `
      gap: 20px;
      padding: 64px 20px 20px 20px;

      @media (min-width: ${sizes.media.tabletPortrait}px) {
        padding: 74px 30px 65px 30px;
      }

      @media (min-width: ${sizes.media.tabletLandscape}px) {
        padding: 20px;
      }

      @media (min-width: ${sizes.media.mediumMaxWidth}px) {
        padding: 20px 25px 55px 25px;
      }
    `,
    [BrandCode.RoyalCaribbean]: `
      gap: 10px;
      padding: 10px 20px 45px 20px;

      @media (min-width: ${sizes.media.tabletPortrait}px) {
        gap: 20px;
        padding: 20px 25px 45px 25px;
      }
    `,
  })[theme.brandCode]}
`;

export const RefinedCruiseLoadingSkeletonShipSocials = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  align-self: stretch;
`;

export const RefinedCruiseLoadingSkeletonShip = styled.div`
  height: 25px;
  flex-shrink: 0;
  background-color: ${({ theme }: { theme: Theme }) => theme.palette.grey[300]};

  ${({ theme }) => ({
    [BrandCode.Celebrity]: `
      width: 201px;
    `,
    [BrandCode.RoyalCaribbean]: `
      width: 187px;
      border-radius: 5px;

      @media (min-width: ${sizes.media.tabletPortrait}px) {
        width: 201px;
      }
    `,
  })[theme.brandCode]}
`;

export const RefinedCruiseLoadingSkeletonSocials = styled.div`
  display: flex;
  height: 28px;
  align-items: center;

  svg {
    path {
      stroke: ${({ theme }) => theme.palette.primary.dark};
      fill: transparent;
    }
  }

  svg:first-child {
    height: 28px;
  }
  
  svg:last-child {
    width: 18px;
    height: 28px;
    margin-left: 18px;
    margin-right: 6px;
  }
`;

export const RefinedCruiseLoadingSkeletonDeparturePort = styled.div`
  width: 240px;
  height: 25px;
  flex-shrink: 0;
  background-color: ${({ theme }: { theme: Theme }) => theme.palette.grey[300]};

  ${({ theme }) => ({
    [BrandCode.RoyalCaribbean]: `
      border-radius: 5px;
    `,
  })[theme.brandCode]}
`;

export const RefinedCruiseLoadingSkeletonLocationList = styled.div`
  align-self: stretch;
  background-color: ${({ theme }: { theme: Theme }) => theme.palette.grey[300]};

  ${({ theme }) => ({
    [BrandCode.Celebrity]: `
      height: 71px;

      @media (min-width: ${sizes.media.tabletPortrait}px) {
        height: 39px;
      }
    
      @media (min-width: ${sizes.media.tabletLandscape}px) {
        height: 84px;
      }
    
      @media (min-width: ${sizes.media.mediumMaxWidth}px) {
        height: 64px;
      }
    `,
    [BrandCode.RoyalCaribbean]: `
      border-radius: 5px;
      height: 45px;

      @media (min-width: ${sizes.media.tabletPortrait}px) {
        height: 50px;
      }
    
      @media (min-width: ${sizes.media.tabletLandscape}px) {
        height: 60px;
      }
    
      @media (min-width: ${sizes.media.mediumMaxWidth}px) {
        height: 44px;
      }
    `,
  })[theme.brandCode]}
`;

export const RefinedCruiseLoadingSkeletonPriceDetail = styled.div`
  display: flex;
  padding-top: 20px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 10px;
  align-self: stretch;
  border-top: 1px solid #D8D8D8;

  @media (min-width: ${sizes.media.tabletPortrait}px) {
    flex-direction: row;
    justify-content: space-between;
    align-items: flex-end;
  }

  ${({ theme }) => ({
    [BrandCode.Celebrity]: `
      @media (min-width: ${sizes.media.tabletLandscape}px) {
        flex-direction: column;
        justify-content: center;
        align-items: center;
      }

      @media (min-width: ${sizes.media.mediumMaxWidth}px) {
        flex-direction: row;
        justify-content: space-between;
        align-items: flex-end;
      }
    `,
  })[theme.brandCode]}
`;

export const RefinedCruiseLoadingSkeletonPrice = styled.div`
  flex-shrink: 0;
  background-color: ${({ theme }: { theme: Theme }) => theme.palette.grey[300]};

  ${({ theme }) => ({
    [BrandCode.Celebrity]: `
      width: 150px;
      height: 85px;

      @media (min-width: ${sizes.media.tabletLandscape}px) {
        height: 69px;
      }

      @media (min-width: ${sizes.media.mediumMaxWidth}px) {
        height: 85px;
      }
    `,
    [BrandCode.RoyalCaribbean]: `
      width: 112px;
      height: 55px;
      border-radius: 5px;

      @media (min-width: ${sizes.media.tabletPortrait}px) {
        width: 150px;
        height: 70px;
      }
    
      @media (min-width: ${sizes.media.tabletLandscape}px) {
        height: 65px;
      }
    
      @media (min-width: ${sizes.media.mediumMaxWidth}px) {
        height: 85px;
      }
    `,
  })[theme.brandCode]}
`;

export const RefinedCruiseLoadingSkeletonButtons = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  align-self: stretch;

  ${({ theme }) => ({
    [BrandCode.Celebrity]: `
      padding: 10px 10px 50px 10px;
      gap: 10px;

      @media (min-width: ${sizes.media.tabletPortrait}px) {
        align-self: unset;
        padding: 0;
      }
    
      @media (min-width: ${sizes.media.tabletLandscape}px) {
        align-self: stretch;
        padding-bottom: 40px;
      }
    
      @media (min-width: ${sizes.media.mediumMaxWidth}px) {
        align-self: unset;
        padding: 0;
      }
    `,
    [BrandCode.RoyalCaribbean]: `
      @media (min-width: ${sizes.media.tabletPortrait}px) {
        align-self: unset;
      }
    `,
  })[theme.brandCode]}
`;

export const RefinedCruiseLoadingSkeletonButton = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  align-self: stretch;
  background-color: ${({ theme }: { theme: Theme }) => theme.palette.grey[300]};

  ${({ theme }) => ({
    [BrandCode.Celebrity]: `
      height: 38px;
      padding: 5px 16px;
      border-radius: 1px;

      @media (min-width: ${sizes.media.tabletPortrait}px) {
        align-self: unset;
        width: 180px;
      }
    
      @media (min-width: ${sizes.media.tabletLandscape}px) {
        align-self: stretch;
        width: unset;
      }
    
      @media (min-width: ${sizes.media.mediumMaxWidth}px) {
        align-self: unset;
        width: 184px;
      }
    `,
    [BrandCode.RoyalCaribbean]: `
      height: 40px;
      padding: 11.5px 50px;
      gap: 10px;
      border-radius: 3px;

      &.hidden-skeleton-button {
        display: none;
      }

      @media (min-width: ${sizes.media.tabletPortrait}px) {
        align-self: unset;
        width: 199px;
        height: 45px;
        padding: 13px 50px;
      }
    
      @media (min-width: ${sizes.media.tabletLandscape}px) {
        padding: 11.5px 50px;
      }
    
      @media (min-width: ${sizes.media.mediumMaxWidth}px) {
        padding: 13px 50px;
      }
    `,
  })[theme.brandCode]}
`;